/**
 * Customer Detail page per COMP-007 Story 3 Step 4.
 * Shows full CustomerConfig, GDAP relationships, active PIM sessions, audit events.
 * Read-only — edits are made in HaloPSA and synced via webhook/timer.
 */

import { useParams, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Building2, Shield, ShieldCheck, FileText } from "lucide-react";
import { GlassCard } from "@/components/sc/glass-card";
import { StatusBadge } from "@/components/sc/status-badge";
import { useCustomerDetail } from "@/hooks/use-customers";
import { useGdapRelationships } from "@/hooks/use-gdap";
import { usePimSessions } from "@/hooks/use-pim-sessions";
import { useRole } from "@/hooks/use-role";

function LoadingSpinner({ resource }: { readonly resource: string }) {
  return (
    <GlassCard className="flex items-center justify-center py-16">
      <div className="text-center">
        <div className="mx-auto mb-2 h-6 w-6 rounded-full bg-sc-primary animate-sc-pulse" />
        <p className="text-body-sm text-sc-text-muted">Loading {resource}...</p>
      </div>
    </GlassCard>
  );
}

function InfoRow({ label, value }: { readonly label: string; readonly value: string | number | null }) {
  return (
    <div className="flex justify-between py-2 border-b border-sc-border/50 last:border-b-0">
      <span className="text-body-sm text-sc-text-muted">{label}</span>
      <span className="text-body-sm text-sc-text font-medium">
        {value ?? "—"}
      </span>
    </div>
  );
}

export function CustomerDetailPage() {
  const { customerId } = useParams({ strict: false }) as { customerId: string };
  const navigate = useNavigate();
  const role = useRole();
  const isAdmin = role === "admin";

  const customerQuery = useCustomerDetail(customerId);
  const gdapQuery = useGdapRelationships({ page: 1, pageSize: 25, customerTenantId: customerId });
  const pimQuery = usePimSessions({ page: 1, pageSize: 25, status: "Active", customerTenantId: customerId });

  const customer = customerQuery.data;

  if (customerQuery.isLoading) {
    return <LoadingSpinner resource="customer" />;
  }

  if (customerQuery.isError || !customer) {
    return (
      <GlassCard className="text-center py-16">
        <Building2 size={48} className="mx-auto mb-4 text-sc-text-muted" />
        <h2 className="text-h3 font-display text-sc-navy mb-2">Customer not found</h2>
        <p className="text-body-sm text-sc-text-muted mb-4">
          Check the link in your HaloPSA ticket.
        </p>
        <button
          onClick={() => void navigate({ to: "/customers" })}
          className="text-body-sm text-sc-primary hover:underline"
        >
          Back to Customers
        </button>
      </GlassCard>
    );
  }

  const gdapRelationships = gdapQuery.data?.items ?? [];

  const pimSessions = pimQuery.data?.items ?? [];

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        onClick={() => void navigate({ to: "/customers" })}
        className="flex items-center gap-1 text-body-sm text-sc-text-muted hover:text-sc-text transition-colors"
      >
        <ArrowLeft size={16} />
        Back to Customers
      </button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-h1 font-display text-sc-navy">
            {customer.customerName}
          </h1>
          <p className="text-body-sm text-sc-text-muted font-mono mt-1">
            {customer.m365TenantId}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={customer.accessType} category="accessType" />
          <StatusBadge status={customer.serviceTier} category="tier" />
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Configuration */}
        <GlassCard>
          <h3 className="text-h3 font-display text-sc-navy mb-3">
            Configuration
          </h3>
          <InfoRow label="Service Tier" value={customer.serviceTier} />
          <InfoRow label="GDAP Strategy" value={customer.accessType} />
          <InfoRow label="Max Access Level" value={customer.maxAccessLevel} />
          <InfoRow label="Session Duration" value={`${customer.sessionDuration}h`} />
          <InfoRow label="PIM Approval Required" value={customer.pimApprovalRequired ? "Yes" : "No"} />
          <InfoRow label="Requires Tier" value={customer.requiresTier} />
          <InfoRow label="Requires Clearance" value={customer.requiresClearance} />
          <InfoRow label="Requires Location" value={customer.requiresLocation} />
          {customer.requiresGroup && (
            <InfoRow label="Required Group" value={customer.requiresGroupName ?? customer.requiresGroup} />
          )}
          {customer.durationDays && (
            <InfoRow label="GDAP Duration Override" value={`${customer.durationDays} days`} />
          )}
          {customer.contactEmail && (
            <InfoRow label="Contact Email" value={customer.contactEmail} />
          )}
          <InfoRow label="Review Frequency" value={customer.reviewFrequency} />
          <InfoRow label="Last Reviewed" value={customer.lastReviewDate ?? "Never"} />
          {customer.accessNotes && (
            <div className="mt-3 pt-3 border-t border-sc-border">
              <span className="text-label text-sc-text-muted uppercase tracking-widest">Notes</span>
              <p className="text-body-sm text-sc-text mt-1">{customer.accessNotes}</p>
            </div>
          )}
        </GlassCard>

        {/* GDAP Relationships */}
        <GlassCard>
          <div className="flex items-center gap-2 mb-3">
            <Shield size={18} className="text-sc-text-muted" />
            <h3 className="text-h3 font-display text-sc-navy">
              GDAP Relationships
            </h3>
          </div>
          {gdapQuery.isLoading ? (
            <p className="text-body-sm text-sc-text-muted">Loading...</p>
          ) : gdapRelationships.length === 0 ? (
            <p className="text-body-sm text-sc-text-muted">
              No GDAP relationships found for this customer.
            </p>
          ) : (
            <div className="space-y-3">
              {gdapRelationships.map((rel) => (
                <div
                  key={rel.id}
                  className="flex items-center justify-between py-2 border-b border-sc-border/50 last:border-b-0"
                >
                  <div>
                    <span className="text-body-sm font-mono text-sc-text-muted">
                      {rel.id.slice(0, 8)}...
                    </span>
                    <p className="text-label text-sc-text-muted">
                      {rel.duration}
                    </p>
                  </div>
                  <StatusBadge status={rel.status} category="gdap" />
                </div>
              ))}
            </div>
          )}
          {isAdmin && (
            <button
              onClick={() => void navigate({ to: "/gdap", search: { createFor: undefined } })}
              className="mt-3 text-body-sm text-sc-primary hover:underline"
            >
              Manage GDAP Relationships
            </button>
          )}
        </GlassCard>

        {/* Active PIM Sessions */}
        <GlassCard>
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={18} className="text-sc-text-muted" />
            <h3 className="text-h3 font-display text-sc-navy">
              Active PIM Sessions
            </h3>
          </div>
          {pimQuery.isLoading ? (
            <p className="text-body-sm text-sc-text-muted">Loading...</p>
          ) : pimSessions.length === 0 ? (
            <p className="text-body-sm text-sc-text-muted">
              No active sessions for this customer.
            </p>
          ) : (
            <div className="space-y-3">
              {pimSessions.map((session) => (
                <div
                  key={session.id}
                  className="flex items-center justify-between py-2 border-b border-sc-border/50 last:border-b-0"
                >
                  <div>
                    <span className="text-body-sm text-sc-text font-medium">
                      {session.technicianName}
                    </span>
                    <p className="text-label text-sc-text-muted">
                      {session.roles.length} role{session.roles.length !== 1 ? "s" : ""}
                    </p>
                  </div>
                  <StatusBadge status={session.status} category="pim" />
                </div>
              ))}
            </div>
          )}
        </GlassCard>

        {/* Access Notes / Actions */}
        <GlassCard>
          <div className="flex items-center gap-2 mb-3">
            <FileText size={18} className="text-sc-text-muted" />
            <h3 className="text-h3 font-display text-sc-navy">
              Quick Actions
            </h3>
          </div>
          <div className="space-y-2">
            <button
              onClick={() =>
                void navigate({
                  to: "/customers/$customerId/activate",
                  params: { customerId },
                })
              }
              className="w-full text-left px-3 py-2 rounded-btn text-body-sm text-sc-text hover:bg-sc-surface-hover transition-colors border border-sc-border"
            >
              Activate PIM Session
            </button>
            {isAdmin && (
              <button
                onClick={() => void navigate({ to: "/gdap", search: { createFor: customerId } })}
                className="w-full text-left px-3 py-2 rounded-btn text-body-sm text-sc-text hover:bg-sc-surface-hover transition-colors border border-sc-border"
              >
                Create GDAP Relationship
              </button>
            )}
          </div>
        </GlassCard>
      </div>
    </div>
  );
}
